import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  MapPin,
  Phone,
  Globe,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Loader2,
  Mail,
  Star
} from 'lucide-react';
import { api } from '../App';
import Chip from '../components/Chip';
import PromotionalContentDisplay from '../components/PromotionalContentDisplay';
import { PromotionalContent } from '../types/promotional-content';
import PageShell from '../components/PageShell';
import StatCard, { StatCardRow } from '../components/StatCard';
import { formatDate } from '../utils/dateUtils';

interface NonProfitDetail {
  id: string;
  charitable_organization_name: string;
  ein?: string;
  registration_number?: string;
  dbas?: string;
  principal_address_1?: string;
  principal_address_2?: string;
  principal_city?: string;
  principal_county?: string;
  principal_state?: string;
  principal_postal_code?: string;
  principal_province?: string;
  principal_country?: string;
  phone?: string;
  email?: string;
  website?: string;
  mailing_address_1?: string;
  mailing_address_2?: string;
  mailing_address_city?: string;
  mailing_address_state?: string;
  mailing_address_postal_code?: string;
  legal_form?: string;
  place_formed?: string;
  date_formed?: string;
  current_status?: string;
  simplified_status?: string;
  status_date?: string;
  expiration_date?: string;
  irs_tax_exempt_status?: string;
  ntee_code_1?: string;
  ntee_code_2?: string;
  ntee_code_3?: string;
  total_revenue?: number;
  total_expenses?: number;
  total_assets_amount?: number;
  total_liabilities_amount?: number;
  net_assets_fund_balance_amount?: number;
  organization_size?: string;
  program_service_ratio?: number;
  fundraising_ratio?: number;
  financial_report_begin_date?: string;
  financial_report_end_date?: string;
  promotional_content?: PromotionalContent[];
  created_at: string;
  updated_at: string;
}

const NonProfitDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [nonprofit, setNonprofit] = useState<NonProfitDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchNonprofit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchNonprofit = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/non-profits/${id}`);
      
      if (response.data.success) {
        setNonprofit(response.data.data);
      } else {
        setError('Failed to load non-profit details');
      }
    } catch (error: any) {
      console.error('Error fetching non-profit:', error);
      if (error.response?.status === 404) {
        setError('Non-profit organization not found');
      } else {
        setError('Failed to load non-profit details');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'current':
        return 'success';
      case 'inactive':
      case 'expired':
        return 'neutral';
      case 'suspended':
      case 'revoked':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getLegalFormColor = (form: string) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800'
    ];
    return colors[Math.abs(form.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % colors.length];
  };


  const formatCurrency = (amount: number, showFull = false) => {
    if (!amount) return '—';
    if (showFull) {
      return `$${amount.toLocaleString()}`;
    }
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toLocaleString()}`;
  };

  const formatPercentage = (ratio: number) => {
    if (!ratio) return '—';
    return `${(ratio * 100).toFixed(1)}%`;
  };

  const getNTEECodes = () => {
    const codes = [nonprofit?.ntee_code_1, nonprofit?.ntee_code_2, nonprofit?.ntee_code_3]
      .filter(code => code && code.trim());
    return codes;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-red-500 text-lg font-medium mb-4">{error}</div>
        </div>
      </div>
    );
  }

  if (!nonprofit) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-text-tertiary dark:text-text-dark-tertiary text-lg font-medium mb-4">Non-profit organization not found</div>
        </div>
      </div>
    );
  }

  return (
    <PageShell
      title={nonprofit.charitable_organization_name}
      description={[
        nonprofit.ein && `EIN: ${nonprofit.ein}`,
        nonprofit.registration_number && `Registration: ${nonprofit.registration_number}`,
      ].filter(Boolean).join(" • ")}
      backUrl="/non-profits"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="card">
            <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4">
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                  Organization Name
                </h3>
                <div className="space-y-1">
                  <p className="text-text-primary dark:text-text-dark-primary font-medium">
                    {nonprofit.charitable_organization_name}
                  </p>
                  {nonprofit.dbas && (
                    <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      <div>DBA: {nonprofit.dbas}</div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                  Status & Legal Form
                </h3>
                <div className="space-y-2">
                  <Chip 
                    label={nonprofit.simplified_status || nonprofit.current_status || 'Unknown'}
                    variant={getStatusVariant(nonprofit.simplified_status || nonprofit.current_status || 'Unknown')}
                    size="sm"
                  />
                  {nonprofit.legal_form && (
                    <div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLegalFormColor(nonprofit.legal_form)}`}>
                        {nonprofit.legal_form}
                      </span>
                    </div>
                  )}
                  {nonprofit.irs_tax_exempt_status && (
                    <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      Tax Status: {nonprofit.irs_tax_exempt_status}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="card">
            <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4">
              Contact Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                  Principal Address
                </h3>
                <div className="space-y-2">
                  {nonprofit.principal_address_1 && (
                    <div className="flex items-start text-neutral-900 dark:text-white">
                      <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div>{nonprofit.principal_address_1}</div>
                        {nonprofit.principal_address_2 && <div>{nonprofit.principal_address_2}</div>}
                        <div>
                          {nonprofit.principal_city && nonprofit.principal_state && (
                            `${nonprofit.principal_city}, ${nonprofit.principal_state}`
                          )}
                          {nonprofit.principal_postal_code && ` ${nonprofit.principal_postal_code}`}
                        </div>
                        {nonprofit.principal_county && (
                          <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                            County: {nonprofit.principal_county}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {!nonprofit.principal_address_1 && (
                    <p className="text-neutral-500 dark:text-text-dark-tertiary">No address provided</p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                    Phone
                  </h3>
                  {nonprofit.phone ? (
                    <div className="flex items-center text-neutral-900 dark:text-white">
                      <Phone className="w-4 h-4 mr-2" />
                      <a
                        href={`tel:${nonprofit.phone}`}
                        className="hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {nonprofit.phone}
                      </a>
                    </div>
                  ) : (
                    <p className="text-neutral-500 dark:text-text-dark-tertiary">No phone provided</p>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                    Email
                  </h3>
                  {nonprofit.email ? (
                    <div className="flex items-center text-neutral-900 dark:text-white">
                      <Mail className="w-4 h-4 mr-2" />
                      <a
                        href={`mailto:${nonprofit.email}`}
                        className="hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {nonprofit.email}
                      </a>
                    </div>
                  ) : (
                    <p className="text-neutral-500 dark:text-text-dark-tertiary">No email provided</p>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                    Website
                  </h3>
                  {nonprofit.website ? (
                    <div className="flex items-center text-neutral-900 dark:text-white">
                      <Globe className="w-4 h-4 mr-2" />
                      <a
                        href={nonprofit.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {nonprofit.website}
                      </a>
                    </div>
                  ) : (
                    <p className="text-neutral-500 dark:text-text-dark-tertiary">No website provided</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          {(nonprofit.total_revenue || nonprofit.total_expenses || nonprofit.total_assets_amount) && (
            <div className="card">
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                Financial Information
              </h2>
              
              <StatCardRow className="mb-6" minTileWidth="12rem">
                {nonprofit.total_revenue && (
                  <StatCard
                    value={formatCurrency(nonprofit.total_revenue)}
                    label="Total Revenue"
                    variant="success"
                    icon={TrendingUp}
                    sublabel={formatCurrency(nonprofit.total_revenue, true)}
                  />
                )}

                {nonprofit.total_expenses && (
                  <StatCard
                    value={formatCurrency(nonprofit.total_expenses)}
                    label="Total Expenses"
                    variant="danger"
                    icon={TrendingDown}
                    sublabel={formatCurrency(nonprofit.total_expenses, true)}
                  />
                )}

                {nonprofit.total_assets_amount && (
                  <StatCard
                    value={formatCurrency(nonprofit.total_assets_amount)}
                    label="Total Assets"
                    variant="info"
                    icon={DollarSign}
                    sublabel={formatCurrency(nonprofit.total_assets_amount, true)}
                  />
                )}
              </StatCardRow>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(nonprofit.program_service_ratio || nonprofit.fundraising_ratio) && (
                  <div>
                    <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                      Efficiency Ratios
                    </h3>
                    <div className="space-y-2">
                      {nonprofit.program_service_ratio && (
                        <div className="flex justify-between">
                          <span className="text-neutral-600 dark:text-text-dark-secondary">Program Services:</span>
                          <span className="font-medium text-neutral-900 dark:text-white">
                            {formatPercentage(nonprofit.program_service_ratio)}
                          </span>
                        </div>
                      )}
                      {nonprofit.fundraising_ratio && (
                        <div className="flex justify-between">
                          <span className="text-neutral-600 dark:text-text-dark-secondary">Fundraising:</span>
                          <span className="font-medium text-neutral-900 dark:text-white">
                            {formatPercentage(nonprofit.fundraising_ratio)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(nonprofit.financial_report_begin_date || nonprofit.financial_report_end_date) && (
                  <div>
                    <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
                      Reporting Period
                    </h3>
                    <div className="space-y-2">
                      {nonprofit.financial_report_begin_date && (
                        <div className="flex items-center text-neutral-900 dark:text-white">
                          <Calendar className="w-4 h-4 mr-2" />
                          <span>From: {formatDate(nonprofit.financial_report_begin_date)}</span>
                        </div>
                      )}
                      {nonprofit.financial_report_end_date && (
                        <div className="flex items-center text-neutral-900 dark:text-white">
                          <Calendar className="w-4 h-4 mr-2" />
                          <span>To: {formatDate(nonprofit.financial_report_end_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NTEE Codes */}
          {getNTEECodes().length > 0 && (
            <div className="card">
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                NTEE Classification
              </h2>
              
              <div className="space-y-2">
                <p className="text-sm text-neutral-600 dark:text-text-dark-secondary mb-3">
                  National Taxonomy of Exempt Entities (NTEE) codes classify the organization&apos;s activities.
                </p>
                <div className="flex flex-wrap gap-2">
                  {getNTEECodes().map((code, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 text-sm font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                    >
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Quick Stats
            </h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-neutral-600 dark:text-text-dark-secondary">Status</span>
                <Chip 
                  label={nonprofit.simplified_status || nonprofit.current_status || 'Unknown'}
                  variant={getStatusVariant(nonprofit.simplified_status || nonprofit.current_status || 'Unknown')}
                  size="sm"
                />
              </div>
              
              {nonprofit.organization_size && (
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600 dark:text-text-dark-secondary">Size</span>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {nonprofit.organization_size}
                    </span>
                  </div>
                </div>
              )}
              
              {nonprofit.principal_state && (
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600 dark:text-text-dark-secondary">State</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {nonprofit.principal_state}
                  </span>
                </div>
              )}

              {nonprofit.legal_form && (
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600 dark:text-text-dark-secondary">Legal Form</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {nonprofit.legal_form}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Important Dates */}
          {(nonprofit.date_formed || nonprofit.status_date || nonprofit.expiration_date) && (
            <div className="card">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                Important Dates
              </h2>
              
              <div className="space-y-3">
                {nonprofit.date_formed && (
                  <div>
                    <span className="text-neutral-600 dark:text-text-dark-secondary text-sm">Date Formed:</span>
                    <div className="font-medium text-neutral-900 dark:text-white">
                      {formatDate(nonprofit.date_formed)}
                    </div>
                  </div>
                )}
                
                {nonprofit.status_date && (
                  <div>
                    <span className="text-neutral-600 dark:text-text-dark-secondary text-sm">Status Date:</span>
                    <div className="font-medium text-neutral-900 dark:text-white">
                      {formatDate(nonprofit.status_date)}
                    </div>
                  </div>
                )}

                {nonprofit.expiration_date && (
                  <div>
                    <span className="text-neutral-600 dark:text-text-dark-secondary text-sm">Expiration Date:</span>
                    <div className="font-medium text-neutral-900 dark:text-white">
                      {formatDate(nonprofit.expiration_date)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System Information */}
          <div className="card">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              System Information
            </h2>
            
            <div className="space-y-3 text-sm">
              {nonprofit.ein && (
                <div>
                  <span className="text-neutral-600 dark:text-text-dark-secondary">EIN:</span>
                  <div className="font-mono text-neutral-900 dark:text-white">
                    {nonprofit.ein}
                  </div>
                </div>
              )}
              
              {nonprofit.registration_number && (
                <div>
                  <span className="text-neutral-600 dark:text-text-dark-secondary">Registration Number:</span>
                  <div className="font-mono text-neutral-900 dark:text-white">
                    {nonprofit.registration_number}
                  </div>
                </div>
              )}

              <div>
                <span className="text-neutral-600 dark:text-text-dark-secondary">Last Updated:</span>
                <div className="text-neutral-900 dark:text-white">
                  {formatDate(nonprofit.updated_at)}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Promotional Content Section */}
      {nonprofit.promotional_content && nonprofit.promotional_content.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white flex items-center">
              <Star className="h-5 w-5 mr-2 text-primary-600" />
              Organization Detail
            </h2>
            <span className="text-sm text-neutral-500 dark:text-text-dark-tertiary font-medium">
              {nonprofit.promotional_content.length} {nonprofit.promotional_content.length === 1 ? 'Highlight' : 'Highlights'}
            </span>
          </div>
          <PromotionalContentDisplay 
            content={nonprofit.promotional_content} 
            entityName={nonprofit.charitable_organization_name}
            entityType="charitable_organization"
            showOrganizationAttribution={true}
          />
        </div>
      )}
    </PageShell>
  );
};

export default NonProfitDetailPage;
